// Sprint composer-draft-attachments-persist — in-memory per-agent store
// for composer attachments (chips). Pairs with the IndexedDB layer at
// `web/src/lib/composer-attachments-idb.ts`.
//
// Why this store exists
// ─────────────────────
// Pre-sprint, chips lived in `useState` inside the Composer component.
// When the user switched agents, the component remounted and the chip
// array was lost. Lifting chip state out of the component is the fix
// for the agent-switch case (the bug Santi reported 2026-05-21).
//
// File objects survive in this store because the SPA does not unload
// between agent switches — the closures hold the live File reference.
// They do NOT survive a page reload (the SPA's JS heap is wiped). The
// IDB layer is responsible for reload survival.
//
// What this store does NOT do
// ───────────────────────────
// - Persist (no zustand/persist middleware). Chips contain `File` and
//   `AbortController` instances that are not JSON-serializable.
// - Cross-tab broadcast. Two tabs editing the same agent get independent
//   in-memory state. The IDB layer is last-write-wins on reload
//   (DAP-5 — pre-existing edge case for the text draft already).

import { create } from 'zustand';
import type { AttachmentChipModel } from '../lib/attachments';

type AgentId = string;

export interface AgentDraftState {
  chips: AttachmentChipModel[];
  // Backend filesystem group id — every chip uploaded as part of the
  // same draft shares this id so the backend can co-locate them under
  // `.asoasis-uploads/<messageId>/`. Regenerated after each successful
  // send.
  messageId: string;
}

export interface ComposerAttachmentsState {
  byAgent: Record<AgentId, AgentDraftState>;

  // Replace the full chip list for an agent. Used on hydrate-from-IDB
  // and after a successful send (clears the list).
  setChips: (agentId: AgentId, chips: AttachmentChipModel[]) => void;

  // Functional update — accepts the previous array, returns the next.
  // Mirrors `setState`'s functional form so the call-site stays familiar
  // to React developers and avoids stale-closure bugs in async upload
  // callbacks (the previous chips array is always read at apply time).
  updateChips: (
    agentId: AgentId,
    updater: (prev: AttachmentChipModel[]) => AttachmentChipModel[],
  ) => void;

  // Set the messageId for this agent's draft. Used when hydrating from
  // IDB (restore persisted messageId so new files share the same backend
  // group) and after a successful send (regenerate).
  setMessageId: (agentId: AgentId, messageId: string) => void;

  // Remove every chip + messageId for an agent. Distinct from
  // `setChips(_, [])` in that it also drops the key from the record so
  // subsequent reads observe "no draft" rather than "empty draft".
  clearAgent: (agentId: AgentId) => void;

  // Wipe the entire store. Called from tests' `afterEach` so the
  // module-level singleton does not leak chips across tests.
  resetForTests: () => void;
}

function emptyDraft(messageId: string): AgentDraftState {
  return { chips: [], messageId };
}

export const useComposerAttachmentsStore = create<ComposerAttachmentsState>(
  (set) => ({
    byAgent: {},

    setChips: (agentId, chips) =>
      set((state) => {
        const prev = state.byAgent[agentId] ?? emptyDraft(crypto.randomUUID());
        return {
          byAgent: { ...state.byAgent, [agentId]: { ...prev, chips } },
        };
      }),

    updateChips: (agentId, updater) =>
      set((state) => {
        const prev = state.byAgent[agentId] ?? emptyDraft(crypto.randomUUID());
        const next = updater(prev.chips);
        return {
          byAgent: { ...state.byAgent, [agentId]: { ...prev, chips: next } },
        };
      }),

    setMessageId: (agentId, messageId) =>
      set((state) => {
        const prev = state.byAgent[agentId] ?? emptyDraft(messageId);
        return {
          byAgent: { ...state.byAgent, [agentId]: { ...prev, messageId } },
        };
      }),

    clearAgent: (agentId) =>
      set((state) => {
        if (!(agentId in state.byAgent)) return state;
        const next = { ...state.byAgent };
        delete next[agentId];
        return { byAgent: next };
      }),

    resetForTests: () => set({ byAgent: {} }),
  }),
);

// Stable empty array reference. Returned by the selector below so React
// treats "no chips for this agent" as a stable value across renders —
// otherwise every render would allocate a new `[]` and downstream
// useMemos would invalidate needlessly.
const EMPTY_CHIPS: readonly AttachmentChipModel[] = Object.freeze([]);

/**
 * Selector hook for the chips of a specific agent. Returns a stable
 * empty array when the agent has no chips, so `useMemo` dependencies
 * remain referentially stable across renders.
 */
export function useChipsForAgent(agentId: AgentId): AttachmentChipModel[] {
  return useComposerAttachmentsStore(
    (s) => s.byAgent[agentId]?.chips ?? (EMPTY_CHIPS as AttachmentChipModel[]),
  );
}

/**
 * Selector hook for the draft's messageId. Returns null when no draft
 * exists yet for the agent (composer should generate one lazily on
 * first admit and write it via `setMessageId`).
 */
export function useDraftMessageId(agentId: AgentId): string | null {
  return useComposerAttachmentsStore(
    (s) => s.byAgent[agentId]?.messageId ?? null,
  );
}
